import { useEffect, useState } from "react";
import { Loader2, MousePointerClick, Eye, MessageCircle, CheckCircle2, Users, Flame } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const PERIODS = [
  { id: "today", label: "Hoje" },
  { id: "7d", label: "7 dias" },
  { id: "30d", label: "30 dias" },
  { id: "custom", label: "Personalizado" },
];

const WA_ORIGINS = {
  float: "Botão flutuante",
  nav: "Menu do site",
  footer: "Rodapé",
  about: "Seção Sobre",
  final: "CTA final",
  chat_result: "Resultado do chatbot",
};

function Card({ icon: Icon, label, value, sub, cls }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`dash-card-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-400">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className={`mt-1 font-display text-2xl font-extrabold ${cls || "text-slate-900"}`}>{value}</p>
      {sub && <p className="text-[11px] font-semibold text-slate-400">{sub}</p>}
    </div>
  );
}

function BarRow({ label, value, max, color }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-28 shrink-0 text-xs font-bold text-slate-500 sm:w-36">{label}</span>
      <div className="h-6 flex-1 overflow-hidden rounded-lg bg-slate-100">
        <div className={`h-full rounded-lg ${color || "bg-brand-600"}`} style={{ width: `${max ? (100 * value) / max : 0}%` }} />
      </div>
      <span className="w-8 text-right text-sm font-bold text-slate-700">{value}</span>
    </div>
  );
}

export default function Dashboard() {
  const [period, setPeriod] = useState("7d");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, [period]);

  async function load() {
    setLoading(true);
    try {
      const params = { period };
      if (period === "custom") {
        if (!start || !end) return setLoading(false);
        params.start = start;
        params.end = end;
      }
      const { data } = await api.get("/admin/dashboard", { params });
      setData(data);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error(formatApiError(e, "Erro ao carregar dashboard."));
    } finally {
      setLoading(false);
    }
  }

  const days = data?.leads_by_day || [];
  const maxDay = Math.max(1, ...days.map((d) => d.count));

  return (
    <div className="space-y-6" data-testid="admin-dashboard-view">
      <div className="flex flex-wrap items-center gap-2 rounded-3xl border border-slate-200 bg-white p-4">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            data-testid={`dash-period-${p.id}`}
            onClick={() => setPeriod(p.id)}
            className={`rounded-full px-4 py-2.5 text-sm font-bold transition-all ${period === p.id ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          >
            {p.label}
          </button>
        ))}
        {period === "custom" && (
          <div className="flex flex-wrap items-center gap-2">
            <input type="date" data-testid="dash-start-date" value={start} onChange={(e) => setStart(e.target.value)} className="h-11 rounded-full border border-slate-200 px-4 text-sm" />
            <span className="text-sm font-bold text-slate-400">até</span>
            <input type="date" data-testid="dash-end-date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-11 rounded-full border border-slate-200 px-4 text-sm" />
            <button onClick={load} data-testid="dash-custom-apply" className="h-11 rounded-full bg-brand-600 px-5 text-sm font-bold text-white">Aplicar</button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : !data ? (
        <p className="py-16 text-center text-sm text-slate-400">Escolha um período personalizado com datas válidas.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Card icon={Eye} label="Visitas" value={data.pageviews} cls="text-slate-900" />
            <Card icon={MousePointerClick} label="Cliques no WhatsApp" value={data.wa_clicks} cls="text-wa" sub={data.wa_by_origin.float ? `${data.wa_by_origin.float} no flutuante` : ""} />
            <Card icon={MessageCircle} label="Aberturas do chat" value={data.chat_opened} />
            <Card icon={CheckCircle2} label="Conversas concluídas" value={data.chat_completed} sub={`Taxa de conclusão: ${data.completion_rate}%`} cls="text-brand-600" />
            <Card icon={Users} label="Leads no período" value={data.leads_in_period} cls="text-brand-600" />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="dash-chart-daily">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Leads por dia</p>
              <div className="mt-4 flex h-36 items-end gap-1.5">
                {days.map((d) => (
                  <div key={d.day} className="group relative flex-1">
                    <div className="w-full rounded-t-md bg-brand-500 transition-all group-hover:bg-brand-600" style={{ height: `${Math.max(4, (100 * d.count) / maxDay) * 1.44}px` }} />
                    <span className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-bold text-white opacity-0 group-hover:opacity-100">
                      {d.count} · {d.day.slice(5)}
                    </span>
                  </div>
                ))}
                {days.length === 0 && <p className="w-full py-10 text-center text-sm text-slate-400">Sem dados no período.</p>}
              </div>
              <div className="mt-2 flex justify-between text-[10px] font-semibold text-slate-400">
                <span>{days[0]?.day.slice(5) || ""}</span><span>{days[days.length - 1]?.day.slice(5) || ""}</span>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2.5 rounded-3xl border border-slate-200 bg-white p-5" data-testid="dash-chart-classification">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Leads por classificação</p>
                <BarRow label="Quente" value={data.leads_by_class.quente} max={data.leads_in_period} color="bg-green-500" />
                <BarRow label="Morno" value={data.leads_by_class.morno} max={data.leads_in_period} color="bg-amber-500" />
                <BarRow label="Frio" value={data.leads_by_class.frio} max={data.leads_in_period} color="bg-slate-400" />
                <BarRow label="Contato" value={data.leads_by_class.contato} max={data.leads_in_period} color="bg-brand-400" />
              </div>
              <div className="space-y-2.5 rounded-3xl border border-slate-200 bg-white p-5" data-testid="dash-chart-category">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Leads por categoria</p>
                <BarRow label="Imóvel" value={data.leads_by_category.imovel} max={data.leads_in_period} color="bg-brand-600" />
                <BarRow label="Veículo" value={data.leads_by_category.veiculo} max={data.leads_in_period} color="bg-brand-300" />
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="dash-wa-origins">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Cliques no WhatsApp por origem</p>
            <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {Object.entries(WA_ORIGINS).map(([k, label]) => (
                <BarRow key={k} label={label} value={(data.wa_by_origin || {})[k] || 0} max={data.wa_clicks} color="bg-wa" />
              ))}
              <BarRow label="Outros" value={(data.wa_by_origin || {}).outros || 0} max={data.wa_clicks} color="bg-slate-300" />
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-3xl border border-amber-200 bg-amber-50 p-5" data-testid="dash-unseen">
            <Flame className="h-6 w-6 shrink-0 text-amber-600" />
            <div>
              <p className="font-display text-lg font-bold text-slate-900">{data.unseen_new_leads} lead(s) novo(s) não visto(s)</p>
              <p className="text-sm text-slate-600">Abra o lead na aba Leads para marcá-lo como visto.</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
